import React, { useEffect, useState, useRef } from "react";
import { SearchIcon } from "./icons";
import background from "./images/background.png";
import { Copy, Check } from "lucide-react";

// Data loader function - fetches ALL quotes, pagination is handled client-side
export const load = async (request) => {
  try {
    const url = new URL(request.url);
    const searchQuery = url.searchParams.get("search");

    if (searchQuery) {
      const response = await fetch(
        `/api/quote/search?search=${encodeURIComponent(searchQuery)}`
      );
      if (!response.ok) throw new Error("Failed to search quotes");
      const data = await response.json();
      return { allQuotes: data, searchTerm: searchQuery };
    } else {
      const response = await fetch("/api/quotes/all");
      if (!response.ok) throw new Error("Failed to fetch quotes");
      const data = await response.json();
      return { allQuotes: data, searchTerm: "" };
    }
  } catch (error) {
    console.error("Error in load function:", error);
    const searchTerm = new URL(request.url).searchParams.get("search") || "";
    return { allQuotes: [], searchTerm, error: error.message };
  }
};

const hostedImages = [
  "https://res.cloudinary.com/dbvqeiqxb/image/upload/w_110,h_110,c_fill,f_auto,q_auto/v1780206630/ChatGPT_Image_May_31_2026_12_47_21_AM_jq7ncn.png",
  "https://res.cloudinary.com/dbvqeiqxb/image/upload/w_110,h_110,c_fill,f_auto,q_auto/v1748135093/karime_norris_uifvic.png",
  "https://res.cloudinary.com/dbvqeiqxb/image/upload/w_110,h_110,c_fill,f_auto,q_auto/v1748135153/kar_drawing_wgyeqi.png",
  "https://res.cloudinary.com/dbvqeiqxb/image/upload/w_110,h_110,c_fill,f_auto,q_auto/v1748135214/kari_coachella_ftuu8k.png",
  "https://res.cloudinary.com/dbvqeiqxb/image/upload/w_110,h_110,c_fill,f_auto,q_auto/v1748135249/kari_coachella_2_tz5ubl.png",
  "https://res.cloudinary.com/dbvqeiqxb/image/upload/w_110,h_110,c_fill,f_auto,q_auto/v1748135302/kari_miami_z94ino.png",
  "https://res.cloudinary.com/dbvqeiqxb/image/upload/w_110,h_110,c_fill,f_auto,q_auto/v1748135353/kari_photoshoot_kb9mss.png",
  "https://res.cloudinary.com/dbvqeiqxb/image/upload/w_110,h_110,c_fill,f_auto,q_auto/v1748135408/kari_brazil_r7ykrz.png",
  "https://res.cloudinary.com/dbvqeiqxb/image/upload/w_110,h_110,c_fill,f_auto,q_auto/v1750547191/karimepindter-june_ocv8iv.png",
  "https://res.cloudinary.com/dbvqeiqxb/image/upload/w_110,h_110,c_fill,f_auto,q_auto/v1750547504/Screenshot_2025-06-21_at_6.11.01_PM_b43a9n.png",
  "https://res.cloudinary.com/dbvqeiqxb/image/upload/w_110,h_110,c_fill,f_auto,q_auto/v1780206566/ChatGPT_Image_May_31_2026_12_46_27_AM_pt7zvh.png",
  "https://res.cloudinary.com/dbvqeiqxb/image/upload/w_110,h_110,c_fill,f_auto,q_auto/v1780206660/ChatGPT_Image_May_31_2026_12_48_11_AM_qzzq6e.png",
];

const CARD_BORDERS = [
  "1.5px solid #7dd3fc", // azul cielo
  "1.5px solid #c4b5fd", // lavanda
  "1.5px solid #f9a8d4", // rosa
];

const getRandomImage = (index) => hostedImages[index % hostedImages.length];

// ── Quote Card ────────────────────────────────────────────────────────────────
const QuoteCard = ({ quote, index }) => {
  const [copied, setCopied] = useState(false);

  const copyQuote = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(quote.text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      className="w-full flex items-center gap-3 mb-2"
      style={{
        background: "rgba(255,255,255,0.82)",
        borderRadius: "20px",
        padding: "14px 16px",
        backdropFilter: "blur(8px)",
        border: CARD_BORDERS[index % CARD_BORDERS.length],
        boxShadow: "0 1px 6px rgba(176,106,179,0.07)",
        transition: "box-shadow 0.15s",
      }}
      onMouseEnter={(e) =>
        (e.currentTarget.style.boxShadow = "0 4px 16px rgba(176,106,179,0.15)")
      }
      onMouseLeave={(e) =>
        (e.currentTarget.style.boxShadow = "0 1px 6px rgba(176,106,179,0.07)")
      }
    >
      {/* Avatar — smaller, left accent */}
      <div
        className="flex-shrink-0"
        style={{
          width: "42px",
          height: "42px",
          borderRadius: "50%",
          overflow: "hidden",
          border: "2px solid rgba(176,106,179,0.3)",
        }}
      >
        <img
          alt="Karime"
          src={getRandomImage(index)}
          loading="lazy"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = hostedImages[0];
          }}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      </div>

      {/* Quote text — takes all space */}
      <p
        className="flex-1 text-sm leading-snug"
        style={{ color: "#2d1f3d", fontWeight: 500 }}
      >
        {quote.text}
      </p>

      {/* Copy button — right side, always visible but subtle */}
      <button
        onClick={copyQuote}
        style={{
          flexShrink: 0,
          width: "32px",
          height: "32px",
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: copied ? "rgba(176,106,179,0.15)" : "transparent",
          border: "none",
          cursor: "pointer",
          transition: "background 0.2s",
          color: copied ? "#b06ab3" : "#c0b0cc",
        }}
        title="Copiar frase"
      >
        {copied ? <Check size={15} /> : <Copy size={15} />}
      </button>
    </div>
  );
};

// ── Search Bar ────────────────────────────────────────────────────────────────
const SearchBar = ({ onSearch, initialSearchTerm = "" }) => {
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    if (
      document.activeElement !== document.querySelector('input[type="text"]')
    ) {
      setSearchTerm(initialSearchTerm);
    }
  }, [initialSearchTerm]);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => onSearch(val), 500);
  };

  useEffect(
    () => () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    },
    []
  );

  return (
    <div className="w-full mb-5 relative">
      <input
        type="text"
        placeholder="Buscar frases..."
        value={searchTerm}
        onChange={handleInputChange}
        style={{
          width: "100%",
          padding: "12px 44px 12px 18px",
          borderRadius: "999px",
          border: "1.5px solid rgba(176,106,179,0.25)",
          background: "rgba(255,255,255,0.9)",
          backdropFilter: "blur(8px)",
          fontSize: "14px",
          color: "#2d1f3d",
          outline: "none",
          boxShadow: "0 1px 8px rgba(176,106,179,0.08)",
          fontFamily: "inherit",
        }}
        onFocus={(e) => (e.target.style.borderColor = "rgba(176,106,179,0.6)")}
        onBlur={(e) => (e.target.style.borderColor = "rgba(176,106,179,0.25)")}
      />
      <span
        style={{
          position: "absolute",
          right: "16px",
          top: "50%",
          transform: "translateY(-50%)",
          color: "#b06ab3",
          pointerEvents: "none",
        }}
      >
        <SearchIcon />
      </span>
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────
const QuoteList = () => {
  const [allQuotes, setAllQuotes] = useState([]);
  const [displayedQuotes, setDisplayedQuotes] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const limit = 10;
  const [visibleCount, setVisibleCount] = useState(limit);

  const fetchData = async (query = "") => {
    setIsLoading(true);
    setError(null);
    try {
      const url = new URL(window.location.href);
      if (query) url.searchParams.set("search", query);
      else url.searchParams.delete("search");

      const data = await load(new Request(url));
      if (data.error) throw new Error(data.error);

      const shuffled = data.allQuotes.sort(() => Math.random() - 0.5);
      setAllQuotes(shuffled);
      setSearchTerm(data.searchTerm);
      setVisibleCount(limit);
      setDisplayedQuotes(shuffled.slice(0, limit));

      const newUrl = new URL(window.location);
      if (data.searchTerm) newUrl.searchParams.set("search", data.searchTerm);
      else newUrl.searchParams.delete("search");
      window.history.pushState({}, "", newUrl);
    } catch (err) {
      setError("Error cargando las frases. Inténtalo de nuevo.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoadMore = () => {
    if (visibleCount >= allQuotes.length) return;
    setLoadingMore(true);
    setTimeout(() => {
      const next = Math.min(visibleCount + limit, allQuotes.length);
      setVisibleCount(next);
      setDisplayedQuotes(allQuotes.slice(0, next));
      setLoadingMore(false);
    }, 300);
  };

  useEffect(() => {
    const searchParam =
      new URLSearchParams(window.location.search).get("search") || "";
    fetchData(searchParam);
  }, []);

  return (
    <div
      className="p-5 flex flex-col min-h-screen font-quicksand"
      style={{
        backgroundImage: `url(${background})`,
        backgroundRepeat: "repeat-y",
        backgroundSize: "100% auto",
        backgroundPosition: "top center",
      }}
    >
      {/* Context line */}
      <div className="text-center mb-4">
        <p className="text-md font-semibold" style={{ color: "#9b7eaa" }}>
          Las frases más icónicas de Karime ✨
        </p>
        <p className="text-sm mt-1" style={{ color: "#c0b0cc" }}>
          ¿cuántas reconoces?
        </p>
      </div>

      <SearchBar onSearch={fetchData} initialSearchTerm={searchTerm} />

      {error && (
        <div
          className="text-sm px-4 py-3 rounded-2xl mb-4"
          style={{ background: "rgba(255,100,100,0.1)", color: "#c0392b" }}
        >
          {error}
          <button onClick={() => fetchData("")} className="ml-2 underline">
            Reintentar
          </button>
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <div
            className="animate-spin rounded-full h-8 w-8 border-2"
            style={{ borderColor: "#b06ab3", borderTopColor: "transparent" }}
          />
        </div>
      ) : (
        <div className="flex-1">
          {allQuotes.length === 0 && !error ? (
            <div className="text-center py-8" style={{ color: "#9b7eaa" }}>
              No se encontraron frases.
              <button
                onClick={() => fetchData("")}
                className="block mx-auto mt-2 underline"
              >
                Ver todas
              </button>
            </div>
          ) : (
            <>
              {displayedQuotes.map((quote, index) => (
                <QuoteCard key={`quote-${index}`} quote={quote} index={index} />
              ))}

              {/* Counter */}
              {allQuotes.length > 0 && (
                <p
                  className="text-center text-xs mt-3 mb-4"
                  style={{ color: "#b0a0ba" }}
                >
                  {displayedQuotes.length} de {allQuotes.length} frases
                </p>
              )}

              {/* Load more */}
              {visibleCount < allQuotes.length && (
                <button
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  className="w-full py-3 rounded-2xl text-sm font-semibold mb-6"
                  style={{
                    background: "rgba(255,255,255,0.8)",
                    color: "#b06ab3",
                    border: "1.5px solid rgba(176,106,179,0.3)",
                    backdropFilter: "blur(8px)",
                    cursor: loadingMore ? "not-allowed" : "pointer",
                  }}
                >
                  {loadingMore ? (
                    <span className="inline-flex items-center gap-2 justify-center">
                      <span
                        className="w-4 h-4 border-2 rounded-full animate-spin"
                        style={{
                          borderColor: "#b06ab3",
                          borderTopColor: "transparent",
                        }}
                      />
                      Cargando...
                    </span>
                  ) : (
                    `Cargar más frases (${
                      allQuotes.length - visibleCount
                    } restantes)`
                  )}
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default QuoteList;
